import { useCallback } from 'react';

import { useError } from '../lib/useError';
import { useAppStore } from '../store/hooks';
import { FlowStep } from '../store/slices/flowSlice';
import { isFieldCompleteByWords } from '../utils/planningHelpers';

// Validation requirements for each step
interface StepValidation {
  isValid: boolean;
  message?: string;
}

export const useFlowNavigation = () => {
  const { flow, flowActions, planningState } = useAppStore();
  const { showError } = useError();

  // Validate if user can proceed from current step
  const validateCurrentStep = useCallback((): StepValidation => {
    switch (flow.currentStep) {
      case FlowStep.LANDING:
        // Landing to Scene Dynamic is always allowed
        return { isValid: true };

      case FlowStep.SCENE_DYNAMIC: {
        // Added curly braces for lexical declarations
        // Scene Dynamic requires type selection and 50+ words in two fields
        const hasType = !!planningState.dynamicType;
        const hasRealWords = isFieldCompleteByWords(planningState.realConversation, 50);
        const hasFictionalWords = isFieldCompleteByWords(planningState.fictionalAnalysis, 50);

        if (!hasType) {
          return {
            isValid: false,
            message: 'Please select a relationship type to continue',
          };
        }
        if (!hasRealWords) {
          return {
            isValid: false,
            message: 'Please write at least 50 words about the real-world example',
          };
        }
        if (!hasFictionalWords) {
          return {
            isValid: false,
            message: 'Please write at least 50 words analyzing the fictional representation',
          };
        }
        return { isValid: true };
      } // Closing curly brace

      case FlowStep.SCENE_PLANNING: {
        // Added curly braces for lexical declarations
        // Scene Planning requires content in all fields
        const requiredFields = [
          { value: planningState.playerRole, name: 'Player Role' },
          { value: planningState.playerName, name: 'Player Name' },
          { value: planningState.playerMotivation, name: 'Player Motivation' },
          { value: planningState.npcRole, name: 'NPC Role' },
          { value: planningState.npcName, name: 'NPC Name' },
          { value: planningState.npcMotivation, name: 'NPC Motivation' },
          { value: planningState.aPlot, name: 'A-Plot' },
          { value: planningState.stakes, name: 'Stakes' },
          { value: planningState.locationAtmosphere, name: 'Location/Atmosphere' },
        ];

        const emptyField = requiredFields.find(
          (field) => !field.value || field.value.trim() === ''
        );
        if (emptyField) {
          return {
            isValid: false,
            message: `Please fill in the ${emptyField.name} field to continue`,
          };
        }
        return { isValid: true };
      } // Closing curly brace

      case FlowStep.EDITOR:
        // Editor is the final step - users can always stay here but cannot progress further
        return { isValid: true };

      default:
        return { isValid: false, message: 'Unknown step' };
    }
  }, [flow.currentStep, planningState]);

  // Navigate to next step in sequence
  const goToNextStep = useCallback(() => {
    const validation = validateCurrentStep();

    if (!validation.isValid) {
      if (validation.message) {
        showError(validation.message);
      }
      return false;
    }

    const steps = Object.values(FlowStep);
    const currentStepIndex = steps.indexOf(flow.currentStep);

    if (currentStepIndex < steps.length - 1) {
      const nextStep = steps[currentStepIndex + 1] as FlowStep;

      // Mark current step as completed
      flowActions.markStepCompleted(flow.currentStep);

      // Navigate to next step
      return flowActions.goToStep(nextStep);
    }

    return false;
  }, [flow.currentStep, flowActions, validateCurrentStep, showError]);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    const steps = Object.values(FlowStep);
    const currentStepIndex = steps.indexOf(flow.currentStep);

    if (currentStepIndex > 0) {
      const previousStep = steps[currentStepIndex - 1] as FlowStep;
      return flowActions.goToStep(previousStep);
    }

    return false;
  }, [flow.currentStep, flowActions]);

  // Navigate to a specific step (if allowed)
  const goToStep = useCallback(
    (step: FlowStep) => {
      if (!flowActions.canNavigateToStep(step)) {
        showError('You must complete previous steps before accessing this section');
        return false;
      }

      return flowActions.goToStep(step);
    },
    [flowActions, showError]
  );

  // Check if current step is complete
  const isCurrentStepComplete = useCallback(() => {
    return validateCurrentStep().isValid;
  }, [validateCurrentStep]);

  // Get progress information
  const getProgress = useCallback(() => {
    const steps = Object.values(FlowStep);
    const currentIndex = steps.indexOf(flow.currentStep);
    const completedCount = flow.completedSteps.size;
    const totalSteps = steps.length;

    return {
      currentStep: flow.currentStep,
      currentIndex,
      completedCount,
      totalSteps,
      percentComplete: Math.round((completedCount / totalSteps) * 100),
      isFirstStep: currentIndex === 0,
      isLastStep: currentIndex === totalSteps - 1,
    };
  }, [flow]);

  return {
    // State
    currentStep: flow.currentStep,
    completedSteps: flow.completedSteps,
    maxAllowedStep: flow.maxAllowedStep,

    // Navigation
    goToNextStep,
    goToPreviousStep,
    goToStep,

    // Validation
    validateCurrentStep,
    isCurrentStepComplete,
    canNavigateToStep: flowActions.canNavigateToStep,

    // Progress
    getProgress,

    // Utilities
    resetFlow: flowActions.resetFlow,
  };
};
